import * as THREE from "three";

export type CityTextureFamily = readonly [
  THREE.Texture,
  THREE.Texture,
  THREE.Texture,
  THREE.Texture,
  THREE.Texture,
];

export interface BuildingSurfaceTextures {
  colors: CityTextureFamily;
  normal: THREE.Texture;
  roughness: THREE.Texture;
}

export interface BuildingGlassTextures {
  colors: CityTextureFamily;
  roughness: THREE.Texture;
  transmission: THREE.Texture;
}

export interface CityTextures {
  brick: BuildingSurfaceTextures;
  stone: BuildingSurfaceTextures;
  glass: BuildingGlassTextures;
  roof: BuildingSurfaceTextures;
  land: THREE.Texture;
  park: THREE.Texture;
  sidewalk: THREE.Texture;
  asphalt: THREE.Texture;
  water: THREE.Texture;
}

interface TextureSpec {
  url: string;
  metresPerRepeat: number;
  colorSpace: THREE.ColorSpace;
}

const color = (url: string, metresPerRepeat: number): TextureSpec => ({
  url,
  metresPerRepeat,
  colorSpace: THREE.SRGBColorSpace,
});
const data = (url: string, metresPerRepeat: number): TextureSpec => ({
  url,
  metresPerRepeat,
  colorSpace: THREE.NoColorSpace,
});

const TEXTURE_SPECS = {
  "brick.color.0": color("/textures/city/building-brick-wall.png", 4),
  "brick.color.1": color("/textures/city/building-brick-wall-2.png", 4),
  "brick.color.2": color("/textures/city/building-brick-wall-3.png", 4),
  "brick.color.3": color("/textures/city/building-brick-wall-4.png", 4),
  "brick.color.4": color("/textures/city/building-brick-wall-5.png", 4),
  "brick.normal": data("/textures/city/building-brick-normal.png", 4),
  "brick.roughness": data("/textures/city/building-brick-roughness.png", 4),
  "stone.color.0": color("/textures/city/building-stone-wall.png", 4),
  "stone.color.1": color("/textures/city/building-stone-wall-2.png", 4),
  "stone.color.2": color("/textures/city/building-stone-wall-3.png", 4),
  "stone.color.3": color("/textures/city/building-stone-wall-4.png", 4),
  "stone.color.4": color("/textures/city/building-stone-wall-5.png", 4),
  "stone.normal": data("/textures/city/building-stone-normal.png", 4),
  "stone.roughness": data("/textures/city/building-stone-roughness.png", 4),
  "glass.color.0": color("/textures/city/building-glass-window.png", 4),
  "glass.color.1": color("/textures/city/building-glass-window-2.png", 4),
  "glass.color.2": color("/textures/city/building-glass-window-3.png", 4),
  "glass.color.3": color("/textures/city/building-glass-window-4.png", 4),
  "glass.color.4": color("/textures/city/building-glass-window-5.png", 4),
  "glass.roughness": data("/textures/city/building-glass-roughness.png", 4),
  "glass.transmission": data("/textures/city/building-glass-transmission.png", 4),
  "roof.color.0": color("/textures/city/building-roof.png", 8),
  "roof.color.1": color("/textures/city/building-roof-2.png", 8),
  "roof.color.2": color("/textures/city/building-roof-3.png", 8),
  "roof.color.3": color("/textures/city/building-roof-4.png", 8),
  "roof.color.4": color("/textures/city/building-roof-5.png", 8),
  "roof.normal": data("/textures/city/building-roof-normal.png", 8),
  "roof.roughness": data("/textures/city/building-roof-roughness.png", 8),
  land: color("/textures/city/ground-land.png", 12),
  park: color("/textures/city/ground-park.png", 8),
  sidewalk: color("/textures/city/ground-sidewalk.png", 4),
  asphalt: color("/textures/city/ground-asphalt.png", 8),
  water: color("/textures/city/ground-water.png", 24),
} as const satisfies Record<string, TextureSpec>;

type TextureSpecKey = keyof typeof TEXTURE_SPECS;

export async function loadCityTextures(maxAnisotropy: number): Promise<CityTextures> {
  const loader = new THREE.TextureLoader();
  const entries = Object.entries(TEXTURE_SPECS) as [TextureSpecKey, TextureSpec][];
  const results = await Promise.allSettled(
    entries.map(async ([key, spec]) => {
      const texture = await loader.loadAsync(spec.url);
      texture.colorSpace = spec.colorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(1 / spec.metresPerRepeat, 1 / spec.metresPerRepeat);
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = Math.min(8, maxAnisotropy);
      return [key, texture] as const;
    }),
  );

  const failedIndex = results.findIndex((result) => result.status === "rejected");
  if (failedIndex !== -1) {
    for (const result of results) {
      if (result.status === "fulfilled") result.value[1].dispose();
    }
    throw new Error(`City texture failed to load: ${entries[failedIndex]![1].url}`);
  }

  const loaded = new Map(
    results.map(
      (result) =>
        (result as PromiseFulfilledResult<readonly [TextureSpecKey, THREE.Texture]>).value,
    ),
  );
  const texture = (key: TextureSpecKey): THREE.Texture => {
    const value = loaded.get(key);
    if (!value) throw new Error(`City texture was not loaded: ${key}`);
    return value;
  };
  const family = (role: "brick" | "stone" | "glass" | "roof"): CityTextureFamily => [
    texture(`${role}.color.0`),
    texture(`${role}.color.1`),
    texture(`${role}.color.2`),
    texture(`${role}.color.3`),
    texture(`${role}.color.4`),
  ];
  return {
    brick: {
      colors: family("brick"),
      normal: texture("brick.normal"),
      roughness: texture("brick.roughness"),
    },
    stone: {
      colors: family("stone"),
      normal: texture("stone.normal"),
      roughness: texture("stone.roughness"),
    },
    glass: {
      colors: family("glass"),
      roughness: texture("glass.roughness"),
      transmission: texture("glass.transmission"),
    },
    roof: {
      colors: family("roof"),
      normal: texture("roof.normal"),
      roughness: texture("roof.roughness"),
    },
    land: texture("land"),
    park: texture("park"),
    sidewalk: texture("sidewalk"),
    asphalt: texture("asphalt"),
    water: texture("water"),
  };
}

export function disposeCityTextures(textures: CityTextures): void {
  const unique = new Set<THREE.Texture>();
  for (const surface of [textures.brick, textures.stone, textures.roof]) {
    for (const texture of surface.colors) unique.add(texture);
    unique.add(surface.normal);
    unique.add(surface.roughness);
  }
  for (const texture of textures.glass.colors) unique.add(texture);
  unique.add(textures.glass.roughness);
  unique.add(textures.glass.transmission);
  unique.add(textures.land);
  unique.add(textures.park);
  unique.add(textures.sidewalk);
  unique.add(textures.asphalt);
  unique.add(textures.water);
  for (const texture of unique) texture.dispose();
}
